import React from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Input from './Input'
import SelectInput from './SelectInput'
import FormDiscardButton from '../buttons/FormDiscardButton'
import FileInput from './FileInput'
import FileDropInput from './FileDropInput'
import FormSubmitWithLoading from '../buttons/FormSubmitWIthLoading'
import InlineRadioInput from './InlineRadioInput'
import FormInputTags from './FormInputTags'
import RatingInput from './RatingInput'
import InlineMultiSelectInput from './InlineMultiSelectInput'
import FormCheckbox from './FormCheckbox'

export default function AbstractForm({
  inputs,
  defaultValues,
  schema,
  actions,
  width,
  handleFormWatch,
  isDisabledSubmit,
  resetOnSubmit,
  stopLoading,
  renderGroup,
  children,
}) {
  const [isLoading, setIsLoading] = React.useState(false)
  const methods = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: schema ? yupResolver(schema) : null,
  })

  const renderInput = (input) => {
    switch (input.renderType) {
      case 'select':
        return <SelectInput {...input} />
      case 'input-tags':
        return <FormInputTags {...input} />
      case 'files':
        return <FileInput {...input} />
      case 'dropzone':
        return <FileDropInput {...input} />
      case 'radio':
        return <InlineRadioInput {...input} />
      case 'rating':
        return <RatingInput {...input} />
      case 'inline-multiselect':
        return <InlineMultiSelectInput {...input} />
      case 'checkbox':
        return <FormCheckbox {...input} />
      default:
        return <Input {...input} />
    }
  }

  const renderCols = (list) => (
    <Row>
      {list.map((input) => (
        <Col key={input.name} sm={input.width}>
          {renderInput(input)}
        </Col>
      ))}
    </Row>
  )

  // When inputs declare a `group` id and a `renderGroup` renderer is provided,
  // consecutive inputs sharing a group are wrapped together (e.g. in a card).
  // Otherwise inputs render flat in a single row (default behaviour).
  const renderFields = () => {
    if (!inputs?.length) return null
    const hasGroups = renderGroup && inputs.some((input) => input.group != null)
    if (!hasGroups) return renderCols(inputs)

    const segments = []
    inputs.forEach((input) => {
      const gid = input.group
      const last = segments[segments.length - 1]
      if (gid == null) {
        if (last && last.type === 'plain') last.inputs.push(input)
        else segments.push({ type: 'plain', inputs: [input] })
      } else if (last && last.type === 'group' && last.id === gid) {
        last.inputs.push(input)
      } else {
        segments.push({ type: 'group', id: gid, inputs: [input] })
      }
    })

    return segments.map((seg, i) =>
      seg.type === 'group' ? (
        renderGroup(seg.id, renderCols(seg.inputs))
      ) : (
        <React.Fragment key={`segment-${i}`}>
          {renderCols(seg.inputs)}
        </React.Fragment>
      ),
    )
  }

  const onSubmit = (data) => {
    setIsLoading(true)
    actions
      .filter((action) => action.field === 'submit')
      .forEach((action) => {
        if (resetOnSubmit) methods.reset(defaultValues)
        action.onClick(data, setIsLoading)
      })
  }

  React.useEffect(() => {
    const subscription = methods.watch((value) => {
      handleFormWatch(value)
    })
    return () => subscription.unsubscribe()
  }, [handleFormWatch, methods, methods.watch])

  React.useEffect(() => {
    if (stopLoading) setIsLoading(false)
  }, [stopLoading])

  React.useEffect(() => {
    methods.reset(defaultValues)
  }, [defaultValues, methods])

  return (
    <Container className="px-0">
      <Row>
        <Col sm={width}>
          <FormProvider {...methods}>
            <Container className="px-0">
              {renderFields()}
              {children ? (
                <Row>
                  <Col>{children}</Col>
                </Row>
              ) : null}
              <Row className="justify-content-end mt-2 g-2">
                {actions?.map((action) => (
                  <Col xs={12} sm="auto" key={action.text}>
                    {action.field === 'submit' ? (
                      <FormSubmitWithLoading
                        text={action.text}
                        isLoading={isLoading}
                        onSubmit={onSubmit}
                        isDisabled={isDisabledSubmit}
                      />
                    ) : (
                      <FormDiscardButton
                        variant="outline-primary"
                        onClick={action.onClick}
                        text={action.text}
                        isLoading={isLoading}
                      />
                    )}
                  </Col>
                ))}
              </Row>
            </Container>
          </FormProvider>
        </Col>
      </Row>
    </Container>
  )
}

AbstractForm.propTypes = {
  inputs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      renderType: PropTypes.string,
      type: PropTypes.string,
      width: PropTypes.number,
      group: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      options: PropTypes.arrayOf(
        PropTypes.shape({
          // eslint-disable-next-line react/forbid-prop-types
          value: PropTypes.any.isRequired,
          // eslint-disable-next-line react/forbid-prop-types
          label: PropTypes.any.isRequired,
        }),
      ),
      helperText: PropTypes.string,
    }),
  ),
  // eslint-disable-next-line react/forbid-prop-types
  defaultValues: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      text: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  width: PropTypes.number,
  children: PropTypes.node,
  handleFormWatch: PropTypes.func,
  isDisabledSubmit: PropTypes.bool,
  resetOnSubmit: PropTypes.bool,
  stopLoading: PropTypes.bool,
  renderGroup: PropTypes.func,
}

AbstractForm.defaultProps = {
  width: 12,
  children: null,
  defaultValues: {},
  schema: null,
  inputs: [],
  handleFormWatch: () => {},
  isDisabledSubmit: false,
  actions: [],
  resetOnSubmit: false,
  stopLoading: false,
  renderGroup: null,
}
